import { useEffect, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { type NoteSuggestion, useConvertNoteItemMutation, useDismissSuggestionMutation } from '@/redux/internal-services/notesApi';
import type { CreateTaskInput } from '@/redux/internal-services/workspaceApiExtensions';
import { TargetIcon } from '@/components/notes/icons';
import TaskForm from '@/components/Tasks/TaskForm';
import { PRIORITY_CFG, STATUS_CFG, TYPE_LABEL, PriorityIcon } from '@/components/Tasks/TaskCard';
import { socketService } from '@/services/SocketService';

const TARGET_LABEL: Record<string, string> = {
  task: 'Task',
  draft_communication: 'Message',
  meeting: 'Meeting',
  link_entity: 'Link',
  property_note: 'Property Note',
  estimate_input: 'Estimate Input',
  proposal_comment: 'Proposal Comment',
};

interface Props {
  /** The note_id these suggestions belong to — needed to convert a suggestion to a draft. */
  noteId: string;
  suggestions: NoteSuggestion[];
  /** suggestion_id → will be created when the page's Done is clicked (default true). */
  included: Record<string, boolean>;
  onToggleIncluded: (suggestionId: string) => void;
  /** Live field edits for a task-target suggestion, read by the batch-create on Done. */
  onFieldsChange: (suggestionId: string, body: CreateTaskInput) => void;
  /** Open an already-created record inline in the workspace (no modal). Task-only for now. */
  onOpenTask?: (taskId: string) => void;
  /** Caller sets this ref so the SAM open_note_followup action can expand a suggestion's
   *  review panel (mirrors clicking "Review") and optionally prefill fields on it. */
  expandRef?: MutableRefObject<((suggestionId: string, fields?: Record<string, unknown>) => void) | null>;
}

/**
 * SAM follow-up suggestions, rendered inline (no modal) using the same visual language as the
 * Tasks list (title, type/priority pills, Open status, assignee). Nothing is created here —
 * a checkbox marks each suggestion for inclusion, "Review" expands it into editable fields
 * (same fields as task creation, no submit button), and the page-level Done action creates
 * every checked, unconfirmed suggestion in one batch (SAM-FEAT-04 / MTG-NFR-02-002: nothing is
 * auto-applied without the user seeing it first).
 */
export function SuggestionCards({ noteId, suggestions, included, onToggleIncluded, onFieldsChange, onOpenTask, expandRef }: Props) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<Record<string, CreateTaskInput>>({});
  // Bumped whenever an external (agent-driven) prefill lands on an already-expanded suggestion —
  // used as part of TaskForm's `key` to force it to pick up the new values. TaskForm is
  // uncontrolled (state seeded once from `initial` via useState), so changing `initial` alone
  // wouldn't update the visible inputs; remounting via key does.
  const [fieldsRev, setFieldsRev] = useState<Record<string, number>>({});
  const [convertItem] = useConvertNoteItemMutation();
  const [dismissSuggestion] = useDismissSuggestionMutation();
  const [openingDraft, setOpeningDraft] = useState<string | null>(null);
  const [localDismissed, setLocalDismissed] = useState<Set<string>>(new Set());

  // Expose an imperative "open for review + optionally prefill" entry point for SAM's
  // open_note_followup tool — mirrors clicking "Review", never creates/confirms anything.
  useEffect(() => {
    if (!expandRef) return;
    expandRef.current = (suggestionId, fields) => {
      setExpandedId(suggestionId);
      if (fields && Object.keys(fields).length) {
        setPreview((prev) => {
          const merged: CreateTaskInput = { ...(prev[suggestionId] || {}), ...fields } as CreateTaskInput;
          onFieldsChange(suggestionId, merged);
          return { ...prev, [suggestionId]: merged };
        });
        setFieldsRev((prev) => ({ ...prev, [suggestionId]: (prev[suggestionId] || 0) + 1 }));
      }
    };
    return () => { expandRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandRef]);

  const dismiss = (id: string) => {
    // Optimistic local dismiss + persist to backend so it survives reloads.
    setLocalDismissed((prev) => new Set(prev).add(id));
    dismissSuggestion({ id: noteId, suggestionId: id }).catch(() => undefined);
  };

  // Open the record created from a suggestion inline in the workspace.
  const openCreated = (s: NoteSuggestion) => {
    const rec = (s.linked_record || {}) as Record<string, unknown>;
    const refType = rec.ref_type ?? rec.target;
    const refId = rec.ref_id;
    if (refType === 'task' && refId) onOpenTask?.(String(refId));
    if (refType === 'communication' && refId) {
      socketService.localEmit('agent:action', {
        message_id: `draft-reopen-${Date.now()}`,
        actions: [{ action_type: 'DRAFT_COMMUNICATION', record_id: String(refId) }],
      });
    }
  };

  // Convert a draft_communication suggestion into a real Communication DRAFT and open the compose card.
  const openAsDraft = async (s: NoteSuggestion) => {
    if (openingDraft) return;
    setOpeningDraft(s.suggestion_id);
    try {
      const payload = (s.payload || {}) as Record<string, unknown>;
      const result = await convertItem({
        id: noteId,
        suggestionId: s.suggestion_id,
        target: 'draft_communication',
        fields: payload as Record<string, unknown>,
      }).unwrap();
      // Find the linked_record to get the created communication_id.
      const updated = (result?.suggestions || []).find(
        (su: NoteSuggestion) => su.suggestion_id === s.suggestion_id
      );
      const commId = (updated?.linked_record as Record<string, unknown> | undefined)?.ref_id;
      const draftPayload = {
        message_id: `draft-open-${Date.now()}`,
        actions: [{ action_type: 'DRAFT_COMMUNICATION', record_id: commId ? String(commId) : undefined }],
      };
      socketService.localEmit('agent:action', draftPayload);
    } catch {
      // ignore — user can retry
    } finally {
      setOpeningDraft(null);
    }
  };

  const visible = suggestions.filter((s) => !s.dismissed && !localDismissed.has(s.suggestion_id));
  if (visible.length === 0) return null;
  const hasPending = visible.some((s) => !s.confirmed);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-ui-text3">
        {t('noteTaker.suggestedFollowups', 'Suggested follow-ups')}
        {hasPending && (
          <span className="font-normal normal-case text-ui-text3"> — {t('noteTaker.reviewBeforeCreating', 'check the ones to create')}</span>
        )}
      </p>
      {visible.map((s) => {
        if (s.confirmed) {
          const label = TARGET_LABEL[s.target || ''] || s.target || s.type;
          return (
            <div
              key={s.suggestion_id}
              className="flex items-center justify-between gap-3 rounded-xl border border-ui-border bg-accent/5 p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs font-medium text-accent-text">
                  <TargetIcon target={s.target} className="h-3.5 w-3.5" />
                  {label}
                </div>
                <p className="mt-0.5 line-clamp-2 text-sm text-ui-text">{s.label}</p>
              </div>
              <button
                type="button"
                onClick={() => openCreated(s)}
                className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-success hover:bg-success/10"
                title={t('noteTaker.openCreated', 'Open created record')}
              >
                {t('noteTaker.created', 'Created')} ✓
              </button>
            </div>
          );
        }

        const isTask = (s.target || 'task') === 'task';
        const payload = (s.payload || {}) as Record<string, unknown>;
        const p = preview[s.suggestion_id];
        const isOpen = expandedId === s.suggestion_id;
        const isIncluded = included[s.suggestion_id] !== false;

        const title = p?.title || (payload.title as string) || s.label;
        const taskType = p?.task_type || (payload.task_type as string) || 'follow_up';
        const priority = p?.priority || (payload.priority as string) || 'normal';
        const dueDate = p?.due_date || (payload.due_date as string) || '';
        const pc = PRIORITY_CFG[priority as keyof typeof PRIORITY_CFG] ?? PRIORITY_CFG.normal;

        return (
          <div
            key={s.suggestion_id}
            className={`rounded-2xl border bg-ui-card overflow-hidden transition-opacity ${isIncluded ? 'border-ui-border' : 'border-ui-border opacity-50'}`}
          >
            <div className="flex items-start gap-2.5 p-3">
              <input
                type="checkbox"
                checked={isIncluded}
                onChange={() => onToggleIncluded(s.suggestion_id)}
                className="mt-1 h-4 w-4 shrink-0 rounded border-ui-border accent-accent-fill"
                aria-label={t('noteTaker.includeSuggestion', 'Create this on Done')}
              />

              <button type="button" onClick={() => setExpandedId(isOpen ? null : s.suggestion_id)} className="min-w-0 flex-1 text-left">
                <div className="flex items-start gap-2">
                  <h4 className="flex-1 truncate text-sm font-semibold text-ui-text">{title}</h4>
                  {isTask && (
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-micro font-bold uppercase tracking-wide ${STATUS_CFG.open.bg} ${STATUS_CFG.open.text}`}>
                      {STATUS_CFG.open.label}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-1 rounded-md border border-ui-border bg-ui-subtle px-2 py-0.5 text-xs font-medium text-ui-text2">
                    <TargetIcon target={s.target} className="h-3 w-3" />
                    {isTask ? (TYPE_LABEL[taskType] || TARGET_LABEL.task) : (TARGET_LABEL[s.target || ''] || s.target)}
                  </span>
                  {isTask && dueDate && (
                    <span className="text-xs text-ui-text3">{new Date(dueDate).toLocaleDateString()}</span>
                  )}
                  {isTask && <span className="text-xs text-ui-text3">{t('noteTaker.assignedMe', 'Me')}</span>}
                  {isTask && (
                    <span className={`ml-auto flex items-center gap-1 ${pc.text}`}>
                      <PriorityIcon level={priority} size={10} />
                      <span className="text-xs font-medium">{pc.label}</span>
                    </span>
                  )}
                </div>
              </button>

              <button
                type="button"
                onClick={() => setExpandedId(isOpen ? null : s.suggestion_id)}
                className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-accent-text hover:bg-accent/10"
              >
                {isOpen ? t('common.collapse', 'Collapse') : t('noteTaker.review', 'Review')}
              </button>
              <button
                type="button"
                onClick={() => dismiss(s.suggestion_id)}
                className="shrink-0 flex h-6 w-6 items-center justify-center rounded-md text-ui-text3 hover:bg-ui-hover hover:text-ui-text transition"
                title={t('common.dismiss', 'Dismiss')}
                aria-label={t('common.dismiss', 'Dismiss')}
              >
                ✕
              </button>
            </div>

            {isOpen && isTask && (
              <div className="border-t border-ui-border p-3">
                <TaskForm
                  // Remount when an external (agent) prefill lands so the uncontrolled form
                  // picks up the new `initial` values instead of keeping whatever it seeded on
                  // first mount.
                  key={`${s.suggestion_id}-${fieldsRev[s.suggestion_id] || 0}`}
                  initial={{
                    // Merge in any local edits / agent prefill (`p`) over the original AI payload
                    // so re-expanding (or an agent edit while already open) doesn't lose them.
                    title: (p?.title as string) || (payload.title as string) || s.label,
                    priority: (p?.priority as string) || (payload.priority as string) || 'normal',
                    dueDate: ((p?.due_date as string) || (payload.due_date as string))?.slice(0, 10) || '',
                  }}
                  hideActions
                  onFieldsChange={(body) => {
                    setPreview((prev) => ({ ...prev, [s.suggestion_id]: body }));
                    onFieldsChange(s.suggestion_id, body);
                  }}
                />
              </div>
            )}

            {isOpen && !isTask && s.target === 'draft_communication' && (
              <div className="border-t border-ui-border p-3 space-y-2">
                {!!payload.channel && (
                  <div className="flex items-center gap-2">
                    <span className="text-micro uppercase tracking-wide text-ui-text3 w-14 shrink-0">Channel</span>
                    <span className="text-xs font-medium text-ui-text capitalize">{String(payload.channel)}</span>
                  </div>
                )}
                {!!payload.to && (
                  <div className="flex items-start gap-2">
                    <span className="text-micro uppercase tracking-wide text-ui-text3 w-14 shrink-0 mt-0.5">To</span>
                    <span className="text-xs text-ui-text">
                      {Array.isArray(payload.to) ? (payload.to as string[]).join(', ') : String(payload.to)}
                    </span>
                  </div>
                )}
                {!!payload.subject && (
                  <div className="flex items-start gap-2">
                    <span className="text-micro uppercase tracking-wide text-ui-text3 w-14 shrink-0 mt-0.5">Subject</span>
                    <span className="text-xs text-ui-text">{String(payload.subject)}</span>
                  </div>
                )}
                {!!payload.body && (
                  <div className="flex items-start gap-2">
                    <span className="text-micro uppercase tracking-wide text-ui-text3 w-14 shrink-0 mt-0.5">Body</span>
                    <p className="text-xs text-ui-text2 line-clamp-4 whitespace-pre-wrap">{String(payload.body)}</p>
                  </div>
                )}
                <div className="pt-1 flex justify-end">
                  <button
                    type="button"
                    disabled={openingDraft === s.suggestion_id}
                    onClick={() => openAsDraft(s)}
                    className="rounded-md btn-sheen px-3 py-1.5 text-xs font-semibold text-on-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {openingDraft === s.suggestion_id
                      ? t('common.loading', 'Opening…')
                      : t('noteTaker.openAsDraft', 'Open as Draft')}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default SuggestionCards;
